/*
 * analysis.js — what a track says beyond its totals: the climbs in it (with a
 * category, as cyclists name them), the fastest stretches over the standard
 * distances, and — when a power meter was on — the best average power over
 * standard durations.
 *
 *   GpsAnalysis.climbs(sr, opts)        → [{ from, to, dist, gain, grade, maxGrade, score, cat, ele0, ele1, sec, vam }]
 *   GpsAnalysis.bestEfforts(sr, dists)  → [{ d, sec, from, to, speed }]
 *   GpsAnalysis.powerCurve(sr, durs)    → { best:[{ s, w, from, to }], np }
 *   GpsAnalysis.gradeExtremes(sr, win)  → { up, down } — steepest % over `win` m (200)
 *   GpsAnalysis.gradeBands(sr)          → Float32Array — grade class per point, for colouring
 *   GpsAnalysis.estimate(sr, {mode, speed}) → { sec, t } — time a route takes on foot / by bike
 *   GpsAnalysis.EFFORTS_FOOT / EFFORTS_RIDE / POWER_DURS
 *
 * Pure functions over a GpsGeo series; no DOM. Each rule says what it follows.
 */
(function () {
    'use strict';
    var root = typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : this);

    /* Categories follow Strava's published rule: climb score = length in metres
     * × average grade in percent; Cat 4 from 8 000, Cat 3 from 16 000, Cat 2
     * from 32 000, Cat 1 from 64 000, HC from 80 000 — and a climb must average
     * at least 3 %. (Alpe d'Huez: 13.8 km × 8.1 % ≈ 112 000 → HC.) */
    var CATS = [[80000, 'HC'], [64000, '1'], [32000, '2'], [16000, '3'], [8000, '4']];
    function category(score) {
        for (var i = 0; i < CATS.length; i++) if (score >= CATS[i][0]) return CATS[i][1];
        return null;
    }

    function lowerBound(a, v, n) { // first index with a[i] >= v
        var lo = 0, hi = n - 1;
        if (!(a[hi] >= v)) return hi;
        while (lo < hi) { var m = (lo + hi) >> 1; if (a[m] < v) lo = m + 1; else hi = m; }
        return lo;
    }

    /* Elevation on a fixed distance grid with the point index at each node —
     * a climb is a property of the road, so it is found in metres, not in
     * fixes (a 1 s and a «smart» recording must find the same climb). */
    function grid(sr, step) {
        var d = sr.dist, e = sr.ele, n = sr.n;
        var first = 0;
        while (first < n && isNaN(e[first])) first++;
        if (first >= n - 1) return null;
        var x0 = d[first], x1 = d[n - 1];
        var m = Math.floor((x1 - x0) / step) + 1;
        if (m < 3) return null;
        var v = new Float64Array(m), idx = new Int32Array(m);
        var j = first;
        for (var k = 0; k < m; k++) {
            var x = x0 + k * step;
            while (j < n - 1 && d[j + 1] < x) j++;
            var a = j, b = Math.min(j + 1, n - 1);
            while (a > first && isNaN(e[a])) a--;
            while (b < n - 1 && isNaN(e[b])) b++;
            var ea = e[a], eb = e[b], val;
            if (isNaN(ea)) val = eb; else if (isNaN(eb) || d[b] === d[a]) val = ea;
            else val = ea + (eb - ea) * Math.max(0, Math.min(1, (x - d[a]) / (d[b] - d[a])));
            v[k] = val;
            idx[k] = Math.abs(d[b] - x) < Math.abs(x - d[a]) ? b : a;
        }
        return { v: v, idx: idx, step: step, x0: x0 };
    }

    function medianF(v, w) {
        if (w <= 1) return v;
        var h = w >> 1, n = v.length, out = new Float64Array(n), buf = [];
        for (var i = 0; i < n; i++) {
            buf.length = 0;
            for (var k = Math.max(0, i - h); k <= Math.min(n - 1, i + h); k++) buf.push(v[k]);
            buf.sort(function (p, q) { return p - q; });
            out[i] = buf[buf.length >> 1];
        }
        return out;
    }
    function meanF(v, w) {
        if (w <= 1) return v;
        var h = w >> 1, n = v.length, out = new Float64Array(n), s = 0, a = 0, b = -1;
        for (var i = 0; i < n; i++) {
            var lo = Math.max(0, i - h), hi = Math.min(n - 1, i + h);
            while (b < hi) s += v[++b];
            while (a < lo) s -= v[a++];
            out[i] = s / (b - a + 1);
        }
        return out;
    }
    function noise(v) {
        if (v.length < 8) return 0;
        var dd = [];
        for (var i = 1; i < v.length - 1; i++) dd.push(Math.abs(v[i + 1] - 2 * v[i] + v[i - 1]));
        dd.sort(function (a, b) { return a - b; });
        return dd[dd.length >> 1] * 1.4826;
    }

    /* Climbs. On a 20 m grid (median, then mean over 100 m — GPS elevation gets
     * a wider median), a climb starts at a low point and runs while the road
     * does not drop back by more than a dip allowance (10 m, up to 8 % of what
     * has been climbed, at most 40 m — a short descent inside a pass does not
     * split it). Flat run-ups and run-outs are trimmed (200 m at under 2 %), and
     * what is left counts when it rises ≥ 20 m over ≥ 300 m at ≥ 3 %. */
    function climbs(sr, opts) {
        opts = opts || {};
        if (!sr || !sr.ele || sr.n < 10) return [];
        var STEP = 20;
        var G = grid(sr, STEP);
        if (!G) return [];
        var raw = G.v;
        var e = meanF(medianF(raw, noise(raw) > 0.6 ? 9 : 5), 5);
        var m = e.length;
        var minGain = opts.minGain != null ? opts.minGain : 20;
        var minLen = opts.minLen != null ? opts.minLen : 300;
        var minGrade = opts.minGrade != null ? opts.minGrade : 3;
        var out = [];
        var low = 0, high = 0, climbing = false;
        function close(a, b) {
            // trim a flat run-up / run-out (200 m under 2 %)
            var W = Math.round(200 / STEP);
            while (a + W < b && (e[a + W] - e[a]) / (W * STEP) < 0.02) a++;
            while (b - W > a && (e[b] - e[b - W]) / (W * STEP) < 0.02) b--;
            var len = (b - a) * STEP, gain = e[b] - e[a];
            if (len < minLen || gain < minGain) return;
            var grade = gain / len * 100;
            if (grade < minGrade) return;
            var mg = -Infinity, W2 = Math.max(1, Math.round(200 / STEP));
            for (var k = a; k + W2 <= b; k++) { var gg = (e[k + W2] - e[k]) / (W2 * STEP) * 100; if (gg > mg) mg = gg; }
            var from = G.idx[a], to = G.idx[b];
            var score = len * grade;
            var c = { from: from, to: to, dist: sr.dist[to] - sr.dist[from], gain: gain, grade: grade, maxGrade: isFinite(mg) ? mg : grade,
                      score: score, cat: category(score), ele0: e[a], ele1: e[b], sec: null, vam: null };
            if (sr.time && !isNaN(sr.time[from]) && !isNaN(sr.time[to]) && sr.time[to] > sr.time[from]) {
                c.sec = (sr.time[to] - sr.time[from]) / 1000;
                c.vam = gain / (c.sec / 3600);
            }
            out.push(c);
        }
        for (var i = 1; i < m; i++) {
            if (!climbing) {
                if (e[i] < e[low]) low = i;
                if (e[i] - e[low] >= 3) { climbing = true; high = i; }
            } else {
                if (e[i] >= e[high]) high = i;
                var gainSoFar = e[high] - e[low];
                var allow = Math.min(40, Math.max(10, gainSoFar * 0.08));
                if (e[high] - e[i] > allow) {
                    close(low, high);
                    climbing = false;
                    low = i;
                    for (var r = high; r <= i; r++) if (e[r] < e[low]) low = r;
                }
            }
        }
        if (climbing) close(low, high);
        return out;
    }

    /* The steepest stretch up and down, over 200 m of road on the same smoothed
     * grid the climbs use. A 60 m window on raw fixes reads a hairpin's GPS
     * jitter as 19 % on a road whose steepest kilometre is 11 %. */
    function gradeExtremes(sr, win) {
        if (!sr || !sr.ele || sr.n < 10) return null;
        var STEP = 20, G = grid(sr, STEP);
        if (!G) return null;
        var e = meanF(medianF(G.v, noise(G.v) > 0.6 ? 9 : 5), 5);
        var W = Math.max(1, Math.round((win || 200) / STEP));
        if (e.length <= W) return null;
        var up = 0, down = 0;
        for (var k = 0; k + W < e.length; k++) {
            var g = (e[k + W] - e[k]) / (W * STEP) * 100;
            if (g > up) up = g;
            if (-g > down) down = -g;
        }
        return { up: up, down: down };
    }

    /* Grade in BANDS for colouring a profile: per-point grade over 100 m of
     * the smoothed road, snapped to classes, and every run shorter than 160 m
     * absorbed by its neighbour. Colouring by the raw grade gave a barcode —
     * hundreds of colour changes on Alpe d'Huez — which reads as noise, not as
     * «the steep part is here». Returns a per-point Float32Array of classes. */
    var BANDS = [-12, -8, -5, -2.5, 0, 2.5, 5, 8, 12];
    function gradeBands(sr) {
        var out = sr && sr.ele ? new Float32Array(sr.n).fill(NaN) : null;
        if (!out || sr.n < 10) return out;
        var STEP = 20, G = grid(sr, STEP);
        if (!G) return out;
        var e = meanF(medianF(G.v, noise(G.v) > 0.6 ? 9 : 5), 5), m = e.length, H = 2, k, i;
        var cls = new Int8Array(m);
        for (k = 0; k < m; k++) {
            var a = Math.max(0, k - H), b = Math.min(m - 1, k + H);
            var g = b > a ? (e[b] - e[a]) / ((b - a) * STEP) * 100 : 0;
            var best = 0, bd = Infinity;
            for (i = 0; i < BANDS.length; i++) { var dd = Math.abs(BANDS[i] - g); if (dd < bd) { bd = dd; best = i; } }
            cls[k] = best;
        }
        // absorb short runs (< 8 cells = 160 m) into the neighbour closer in class
        for (var pass = 0; pass < 4; pass++) {
            var runs = [], s0 = 0;
            for (k = 1; k <= m; k++) if (k === m || cls[k] !== cls[s0]) { runs.push([s0, k - 1, cls[s0]]); s0 = k; }
            if (runs.length < 2) break;
            var changed = false;
            for (var r = 0; r < runs.length; r++) {
                var R = runs[r];
                if (R[1] - R[0] + 1 >= 8) continue;
                var L2 = runs[r - 1], N2 = runs[r + 1], to;
                if (!L2) to = N2[2]; else if (!N2) to = L2[2];
                else to = Math.abs(L2[2] - R[2]) <= Math.abs(N2[2] - R[2]) ? L2[2] : N2[2];
                for (k = R[0]; k <= R[1]; k++) cls[k] = to;
                R[2] = to; changed = true;
            }
            if (!changed) break;
        }
        // points take the class of their grid cell
        var d = sr.dist, x0 = G.x0;
        for (i = 0; i < sr.n; i++) {
            var c = Math.max(0, Math.min(m - 1, Math.round((d[i] - x0) / STEP)));
            out[i] = BANDS[cls[c]];
        }
        return out;
    }

    /* How long a route takes, when the file has no time (a planned route, a
     * track drawn on a map). On foot — Tobler's hiking function scaled to the
     * person's flat pace (6·e^(−3.5·|s+0.05|) km/h: fastest slightly downhill,
     * 1 km/h at ±70 %). By bike — P = v·m·g·(Crr·cosθ + sinθ) + ½·ρ·CdA·v³
     * solved for v, where P is the power that holds the flat speed, raised on
     * climbs the way people actually ride (×2.2 from 8 %: nobody climbs at
     * their cruising watts) and eased on descents; never slower than 4 km/h
     * (walking the bike) nor faster than 55 km/h downhill. At 20 km/h on the
     * flat that puts Alpe d'Huez (13.7 km, 8 %) at about 1 h 45 min — the
     * range recreational riders report. Grade over 100 m of road.
     *   estimate(sr, { mode:'foot'|'bike', speed: m/s on the flat })
     *     → { sec, t: Float64Array seconds from the start per point } */
    var RIDER = { m: 85, crr: 0.006, cda: 0.45, rho: 1.2, g: 9.81 };
    function bikeSpeed(grade, P) {
        var th = Math.atan(grade), R = RIDER;
        var a = 0.5 * R.rho * R.cda, b = R.m * R.g * (R.crr * Math.cos(th) + Math.sin(th));
        var lo = 0.3, hi = 30; // bisection on f(v) = a·v³ + b·v − P, monotone for v > 0 when b > −3a·v²
        for (var i = 0; i < 50; i++) {
            var mid = (lo + hi) / 2, f = a * mid * mid * mid + b * mid - P;
            if (f > 0) hi = mid; else lo = mid;
        }
        return Math.max(4 / 3.6, Math.min(55 / 3.6, (lo + hi) / 2));
    }
    function estimate(sr, opts) {
        opts = opts || {};
        var mode = opts.mode === 'bike' ? 'bike' : 'foot';
        var v0 = opts.speed || (mode === 'bike' ? 20 / 3.6 : 5 / 3.6);
        var n = sr ? sr.n : 0, t = new Float64Array(n);
        if (n < 2) return { sec: 0, t: t };
        var gr = null;
        if (sr.ele) {
            gr = new Float32Array(n).fill(0);
            var d = sr.dist, e = sr.ele, a = 0, b = 0;
            for (var i = 0; i < n; i++) { // grade over ±50 m
                while (a < i && d[i] - d[a] > 50) a++;
                if (b < i) b = i;
                while (b + 1 < n && d[b + 1] - d[i] <= 50) b++;
                var aa = a, bb = b;
                while (aa < i && isNaN(e[aa])) aa++;
                while (bb > i && isNaN(e[bb])) bb--;
                var dd = d[bb] - d[aa];
                if (dd > 20 && !isNaN(e[aa]) && !isNaN(e[bb])) gr[i] = Math.max(-0.7, Math.min(0.7, (e[bb] - e[aa]) / dd));
            }
        }
        var P = mode === 'bike' ? (RIDER.m * RIDER.g * RIDER.crr + 0.5 * RIDER.rho * RIDER.cda * v0 * v0) * v0 : 0;
        var tob0 = Math.exp(-3.5 * 0.05), acc = 0;
        for (var j = 1; j < n; j++) {
            var dx = sr.geoDist[j] - sr.geoDist[j - 1];
            if (!(dx > 0)) { t[j] = acc; continue; }
            var s = gr ? (gr[j - 1] + gr[j]) / 2 : 0, v;
            if (mode === 'bike') v = bikeSpeed(s, P * (s > 0 ? 1 + Math.min(1.2, s * 15) : Math.max(0, 1 + s * 10)));
            else v = Math.max(0.28, v0 * Math.exp(-3.5 * Math.abs(s + 0.05)) / tob0);
            acc += dx / v;
            t[j] = acc;
        }
        return { sec: acc, t: t };
    }

    /* Fastest stretch over a distance, as Strava's «best efforts» do it: the
     * least ELAPSED time between two points the given distance apart, the
     * start interpolated between fixes so the distance is exact. */
    var EFFORTS_FOOT = [400, 804.672, 1000, 1609.344, 3218.688, 5000, 10000, 15000, 16093.44, 20000, 21097.5, 30000, 42195];
    var EFFORTS_RIDE = [5000, 10000, 20000, 40000, 50000, 80000, 100000, 160934.4];
    function bestEfforts(sr, dists) {
        var out = [];
        if (!sr || !sr.time || sr.n < 3) return out;
        var d = sr.dist, t = sr.time, n = sr.n;
        var total = d[n - 1] - d[0];
        (dists || EFFORTS_FOOT).forEach(function (D) {
            if (D > total) return;
            var best = Infinity, bi = -1, bs = -1, j = 0;
            for (var i = 1; i < n; i++) {
                if (isNaN(t[i])) continue;
                var target = d[i] - D;
                if (target < d[0]) continue;
                while (j + 1 < i && d[j + 1] <= target) j++;
                if (isNaN(t[j]) || j + 1 >= n || isNaN(t[j + 1])) continue;
                var span = d[j + 1] - d[j];
                var ts = span > 0 ? t[j] + (t[j + 1] - t[j]) * (target - d[j]) / span : t[j];
                var el = (t[i] - ts) / 1000;
                if (el > 0 && el < best) { best = el; bi = i; bs = j; }
            }
            if (bi >= 0) out.push({ d: D, sec: best, from: bs, to: bi, speed: D / best });
        });
        return out;
    }

    /* Best average power over standard durations, on a 1 s grid. A fix holds
     * its power for up to 5 s (smart recording); a longer gap is a stop and
     * counts as zero, so a window can never borrow effort across a pause.
     * Normalized power: 30 s rolling mean, 4th power, mean, 4th root. */
    var POWER_DURS = [5, 15, 30, 60, 120, 300, 600, 1200, 1800, 3600];
    function powerCurve(sr, durs) {
        var P = sr && sr.ch && sr.ch.power, t = sr && sr.time;
        if (!P || !t) return null;
        var n = sr.n, t0 = NaN, t1 = NaN, i;
        for (i = 0; i < n; i++) if (!isNaN(t[i]) && !isNaN(P[i])) { t0 = t[i]; break; }
        for (i = n - 1; i >= 0; i--) if (!isNaN(t[i]) && !isNaN(P[i])) { t1 = t[i]; break; }
        if (!(t1 > t0)) return null;
        var S = Math.floor((t1 - t0) / 1000) + 1;
        if (S < 5 || S > 7 * 86400) return null;
        var p1 = new Float32Array(S), at = new Int32Array(S);
        var k = 0;
        for (var s = 0; s < S; s++) {
            var ts = t0 + s * 1000;
            while (k + 1 < n && (isNaN(t[k + 1]) || t[k + 1] <= ts)) k++;
            var ok = !isNaN(t[k]) && !isNaN(P[k]) && ts - t[k] <= 5000 && ts >= t[k];
            p1[s] = ok ? P[k] : 0;
            at[s] = k;
        }
        var best = [];
        (durs || POWER_DURS).forEach(function (D) {
            if (D > S) return;
            var sum = 0, mx = -1, mi = 0;
            for (var q = 0; q < S; q++) {
                sum += p1[q];
                if (q >= D) sum -= p1[q - D];
                if (q >= D - 1 && sum > mx) { mx = sum; mi = q; }
            }
            if (mx > 0) best.push({ s: D, w: mx / D, from: at[mi - D + 1], to: at[mi] });
        });
        var np = null;
        if (S >= 300) {
            var sum30 = 0, acc = 0, cnt = 0;
            for (var r = 0; r < S; r++) {
                sum30 += p1[r];
                if (r >= 30) sum30 -= p1[r - 30];
                if (r >= 29) { var avg = sum30 / 30; acc += avg * avg * avg * avg; cnt++; }
            }
            if (cnt) np = Math.pow(acc / cnt, 0.25);
        }
        return { best: best, np: np };
    }

    root.GpsAnalysis = {
        climbs: climbs, bestEfforts: bestEfforts, powerCurve: powerCurve, category: category, gradeExtremes: gradeExtremes, gradeBands: gradeBands, estimate: estimate,
        EFFORTS_FOOT: EFFORTS_FOOT, EFFORTS_RIDE: EFFORTS_RIDE, POWER_DURS: POWER_DURS
    };
})();
